import { STAFF_ROLES } from '@cf/shared';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router';

import { AuthShell } from '../components/AuthShell';
import { homeFor } from '../components/RequireAuth';
import { Field, inputClass } from '../components/ui';
import { ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApi } from '../lib/hooks';

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const setup = useApi<{ needsOwner: boolean }>('/setup/status');

  if (setup.data?.needsOwner) return <Navigate to="/setup" replace />;
  if (user && STAFF_ROLES.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const u = await login(email, password);
      if (!STAFF_ROLES.includes(u.role)) {
        await logout();
        setError(t('auth.forbidden'));
        return;
      }
      const from = (location.state as { from?: string } | null)?.from;
      await navigate(from ?? homeFor(u.role), { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401 ? t('auth.invalid') : t('auth.genericError'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell
      aside={
        <select
          aria-label={t('common.language')}
          value={i18n.language}
          onChange={(e) => void i18n.changeLanguage(e.target.value)}
          className="h-8 rounded-lg border-0 bg-surface px-2.5 text-[13px] text-stone-700 shadow-xs ring-1 ring-inset ring-stone-300 focus:outline-none focus:ring-2 focus:ring-copper-500"
        >
          <option value="fr">Français</option>
          <option value="ar">العربية</option>
        </select>
      }
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-stone-900">
            {t('auth.title')}
          </h1>
          <p className="mt-1.5 text-sm text-stone-500">{t('login.subtitle')}</p>
        </div>
        <div className="space-y-4">
          <Field label={t('auth.email')}>
            <input
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`${inputClass} h-11 text-base`}
            />
          </Field>
          <Field label={t('auth.password')}>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`${inputClass} h-11 text-base`}
            />
          </Field>
        </div>
        {error && (
          <p
            role="alert"
            className="rounded-lg bg-red-50 px-3 py-2.5 text-sm text-red-700 ring-1 ring-inset ring-red-600/10"
          >
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="h-11 w-full rounded-lg bg-copper-600 text-[15px] font-semibold text-on-fill shadow-xs ring-1 ring-inset ring-copper-700/20 transition-colors hover:bg-copper-700 disabled:opacity-60"
        >
          {submitting ? t('auth.submitting') : t('auth.submit')}
        </button>
      </form>
    </AuthShell>
  );
}
